import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs';
import { ComponentWithDestroy } from 'src/app/modules/shared/component-with-destroy';
import { BROWSER_NAVIGATE } from '../browser-navigate.injection-token';
import { ForcedPasswordResetService } from './forced-password-reset.service';

const MIN_LENGTH = 15;

export interface PasswordRule {
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  { label: `At least ${MIN_LENGTH} characters`, test: password => password.length >= MIN_LENGTH },
  { label: 'Lower case letters (a-z)', test: password => /[a-z]/.test(password) },
  { label: 'Upper case letters (A-Z)', test: password => /[A-Z]/.test(password) },
  { label: 'Numbers (0-9)', test: password => /\d/.test(password) },
];

const passwordsMatch = (group: AbstractControl): ValidationErrors | null =>
  group.get('newPassword')?.value === group.get('confirmPassword')?.value ? null : { mismatch: true };

/**
 * First-login password change. Auth0's Post-Login Action redirects here (with `session_token`
 * and `state`); once the password is set, the login is resumed on Auth0's /continue endpoint.
 */
@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordComponent extends ComponentWithDestroy {
  readonly rules = PASSWORD_RULES;
  readonly sessionToken: string;
  readonly state: string;
  readonly form = this.fb.nonNullable.group(
    {
      newPassword: ['', [Validators.required, ...PASSWORD_RULES.map(rule => this.ruleValidator(rule))]],
      confirmPassword: ['', Validators.required],
    },
    { validators: passwordsMatch },
  );
  submitting = false;
  errorMessage = '';

  constructor(
    private fb: FormBuilder,
    route: ActivatedRoute,
    private resetService: ForcedPasswordResetService,
    private cdr: ChangeDetectorRef,
    @Inject(BROWSER_NAVIGATE) private navigate: (url: string) => void,
  ) {
    super();
    this.sessionToken = route.snapshot.queryParamMap.get('session_token') ?? '';
    this.state = route.snapshot.queryParamMap.get('state') ?? '';
  }

  get hasSession(): boolean {
    return !!this.sessionToken && !!this.state;
  }

  get passwordsMismatch(): boolean {
    return this.form.hasError('mismatch') && this.form.controls.confirmPassword.dirty;
  }

  ruleMet(rule: PasswordRule): boolean {
    return rule.test(this.form.controls.newPassword.value);
  }

  trackByLabel(_index: number, rule: PasswordRule): string {
    return rule.label;
  }

  submit(): void {
    if (this.form.invalid || this.submitting) {
      return;
    }
    this.submitting = true;
    this.errorMessage = '';
    this.resetService
      .resetPassword(this.sessionToken, this.state, this.form.controls.newPassword.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: token => this.navigate(this.resetService.buildContinueUrl(this.state, token)),
        error: (error: Error) => {
          this.errorMessage = error.message;
          this.submitting = false;
          this.cdr.markForCheck();
        },
      });
  }

  private ruleValidator(rule: PasswordRule) {
    return (control: AbstractControl): ValidationErrors | null => (rule.test(control.value as string) ? null : { rule: rule.label });
  }
}
